import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as CloseIcon } from 'assets/icons/ic_close.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/ic_search.svg';
import MoveInModal from 'components/search/MoveInModal';
import WhereModal from 'components/search/WhereModal';
import { useGetWhereSearch } from 'entities/home';
import { useWhereSearchStore } from 'features/home';
import { Minus, Plus } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { formatLocalizedDate, formatLocalizedPeriod } from 'shared/lib';
import { twJoin, twMerge } from 'tailwind-merge';

export type MobileSearchParams = {
  originalWhere?: string;
  whereType?: string;
  whereValue?: string;
  moveIn?: string;
  contractPeriod?: string;
};

type SearchType = 'WHERE' | 'MOVEIN' | 'PERIOD' | null;

type Props = {
  onSearch: (params: MobileSearchParams) => void;
  defaultContractPeriod?: string;
  searchSource?: string;
  showSubtitle?: boolean;
};

const PERIOD_PRESETS = [3, 6, 12];
const PERIOD_MIN = 1;
const PERIOD_MAX = 24;

const MobileSearchBottomSheet = ({
  onSearch,
  defaultContractPeriod = '',
  searchSource = 'main page',
  showSubtitle = false,
}: Props) => {
  const { t, i18n } = useTranslation('search');
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState<SearchType>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const [originalWhere, setOriginalWhere] = useState('');
  const [whereType, setWhereType] = useState('');
  const [whereValue, setWhereValue] = useState('');
  const [moveIn, setMoveIn] = useState('');
  const [contractPeriod, setContractPeriod] = useState(defaultContractPeriod);
  const [displayPeriod, setDisplayPeriod] = useState('');

  const { addWhereSearch, whereSearches } = useWhereSearchStore();
  const { data: whereData } = useGetWhereSearch(originalWhere);

  // 마지막으로 URL 에서 동기화한 검색바 파라미터 시그니처 (불필요한 덮어쓰기 방지용 — 아래 effect 참고)
  const lastUrlSyncRef = useRef<string | null>(null);

  const handleChangePeriod = (value: number) => {
    setContractPeriod(value.toString());
    setDisplayPeriod(formatLocalizedPeriod(value, t('months')));
  };

  // URL → 내부 상태 복원 (페이지 진입·공유 URL 재접속·뒤로가기 시).
  // 단, 지도 bounds·필터·정렬 등 검색바와 무관한 URL 변경으로 이 effect 가 재실행될 때
  // 아직 검색(확정)하지 않은 사용자의 입력값을 옛 URL 값으로 덮어쓰지 않도록,
  // 검색바 관련 파라미터가 실제로 바뀐 경우에만 동기화한다.
  useEffect(() => {
    const ow = searchParams.get('originalWhere');
    const wt = searchParams.get('whereType');
    const wv = searchParams.get('whereValue');
    const mi = searchParams.get('moveIn');
    const cp = searchParams.get('contractPeriod');

    const signature = JSON.stringify([ow, wt, wv, mi, cp]);
    if (lastUrlSyncRef.current === signature) return;
    lastUrlSyncRef.current = signature;

    if (ow !== null) setOriginalWhere(ow);
    if (wt !== null) setWhereType(wt);
    if (wv !== null) setWhereValue(wv);
    if (mi !== null) setMoveIn(mi);
    if (cp !== null) {
      setContractPeriod(cp);
      setDisplayPeriod(`${cp} ${t('months')}`);
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (active === null) {
      if ((whereType === '' || whereValue === '') && originalWhere !== '') {
        setOriginalWhere('');
      }
    }
  }, [active, whereType, whereValue, originalWhere]);

  useEffect(() => {
    if (contractPeriod && !displayPeriod) {
      setDisplayPeriod(formatLocalizedPeriod(Number(contractPeriod), t('months')));
    }
  }, [contractPeriod, displayPeriod, t]);

  useEffect(() => {
    if (isExpanded) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  const handlePeriodDelta = (delta: number) => {
    const current = Number(contractPeriod) || PERIOD_MIN;
    const next = Math.min(PERIOD_MAX, Math.max(PERIOD_MIN, current + delta));
    handleChangePeriod(next);
  };

  const handleSearch = () => {
    window.dataLayer?.push({
      event: 'search',
      where: originalWhere === '' ? undefined : originalWhere,
      move_in: moveIn === '' ? undefined : moveIn,
      contract_period: contractPeriod === '' ? undefined : contractPeriod,
      search_term: originalWhere === '' ? undefined : originalWhere,
      type: searchSource,
    });

    onSearch({
      originalWhere: originalWhere || undefined,
      whereType: whereType || undefined,
      whereValue: whereValue || undefined,
      moveIn: moveIn || undefined,
      contractPeriod: contractPeriod || undefined,
    });

    if (whereType && whereValue && originalWhere) {
      addWhereSearch({ id: Date.now(), whereType, whereValue, originalValue: originalWhere });
    }

    setIsExpanded(false);
    setActive(null);
  };

  const cardClass =
    'flex w-full flex-col gap-y-1 rounded-[14px] bg-white px-4 py-3 text-left shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]';
  const activeCardClass = 'ring-1 ring-primary';
  const panelClass = 'w-full rounded-[14px] bg-white p-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]';

  return (
    <>
      {/* Pill trigger (collapsed) */}
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="flex h-14 w-full items-center gap-3 rounded-full bg-white px-6 text-left shadow-search md:hidden"
      >
        <SearchIcon width={20} height={20} className="shrink-0 text-[#8E8E8E]" />
        <div className={twJoin('flex min-w-0 flex-1', showSubtitle && 'flex-col items-start leading-tight')}>
          <span
            className={twJoin(
              'w-full truncate text-[15px] font-medium',
              originalWhere ? 'text-[#222]' : 'text-[#8E8E8E]',
            )}
          >
            {originalWhere || t('searchByLocation')}
          </span>
          {showSubtitle && (
            <span className="w-full truncate text-[12px] text-[#8E8E8E]">
              {`${moveIn ? formatLocalizedDate(moveIn, i18n.language) : t('moveInDate')} · ${displayPeriod || t('contractPeriod')}`}
            </span>
          )}
        </div>
      </button>

      {/* Modal sheet (expanded) */}
      {isExpanded &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[99] bg-black/30 md:hidden"
              onClick={() => {
                setIsExpanded(false);
                setActive(null);
              }}
            />
            {/* Sheet */}
            <div className="fixed inset-x-0 bottom-0 top-12 z-[100] flex flex-col overflow-hidden rounded-t-[20px] bg-[#F4F4F4] md:hidden">
              {/* Header */}
              <div className="relative flex shrink-0 items-center justify-center px-4 py-4">
                <h2 className="text-[17px] font-semibold text-[#222]">{t('search')}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setIsExpanded(false);
                    setActive(null);
                  }}
                  aria-label="Close"
                  className="absolute right-3 flex h-8 w-8 items-center justify-center"
                >
                  <CloseIcon width={18} height={18} />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 pb-2 pt-2">
                {/* Where field group */}
                {active === 'WHERE' ? (
                  <div className={twMerge(cardClass, activeCardClass)}>
                    <span className="text-[14px] font-semibold text-[#222]">{t('where')}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        autoFocus
                        className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-gray-40"
                        placeholder={t('searchPlaces')}
                        value={originalWhere}
                        onChange={(e) => {
                          setOriginalWhere(e.target.value);
                          setWhereValue('');
                          setWhereType('');
                        }}
                      />
                      {originalWhere && (
                        <button
                          type="button"
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#727272] text-white opacity-50"
                          onClick={() => {
                            setOriginalWhere('');
                            setWhereValue('');
                            setWhereType('');
                          }}
                        >
                          <CloseIcon width={10} height={10} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setActive('WHERE')} className={cardClass}>
                    <span className="text-[14px] font-semibold text-[#222]">{t('where')}</span>
                    <span className={`text-[14px] ${originalWhere ? 'text-[#222]' : 'text-gray-40'}`}>
                      {originalWhere || t('searchPlaces')}
                    </span>
                  </button>
                )}
                {active === 'WHERE' && (
                  <div className={`${panelClass} max-h-[50vh] overflow-y-auto`}>
                    <WhereModal
                      whereSearches={whereSearches}
                      whereData={whereData}
                      originalValue={originalWhere}
                      setOriginalValue={setOriginalWhere}
                      setWhereValue={setWhereValue}
                      setWhereType={setWhereType}
                      onClose={() => setActive(null)}
                    />
                  </div>
                )}

                {/* MoveIn field group */}
                <button
                  type="button"
                  onClick={() => setActive(active === 'MOVEIN' ? null : 'MOVEIN')}
                  className={twMerge(cardClass, active === 'MOVEIN' && activeCardClass)}
                >
                  <span className="text-[14px] font-semibold text-[#222]">{t('moveIn')}</span>
                  <span className={`text-[14px] ${moveIn ? 'text-[#222]' : 'text-gray-40'}`}>
                    {moveIn ? formatLocalizedDate(moveIn, i18n.language) : t('addDate')}
                  </span>
                </button>
                {active === 'MOVEIN' && (
                  <div className={panelClass}>
                    <MoveInModal value={moveIn} setValue={setMoveIn} onClose={() => setActive(null)} />
                  </div>
                )}

                {/* Period field - always inline with counter + presets */}
                <div className={twMerge(cardClass, 'gap-y-4')}>
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] font-semibold text-[#222]">{t('contractPeriod')}</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handlePeriodDelta(-1)}
                        disabled={Number(contractPeriod) <= PERIOD_MIN}
                        className={twJoin(
                          'flex h-5 w-5 items-center justify-center rounded-full border',
                          Number(contractPeriod) <= PERIOD_MIN
                            ? 'border-gray-20 text-gray-20'
                            : 'border-primary text-primary',
                        )}
                        aria-label={t('decreasePeriod')}
                      >
                        <Minus width={10} height={10} />
                      </button>
                      <span className="min-w-[24px] text-center text-[14px] font-semibold text-[#222]">
                        {contractPeriod ? `${contractPeriod}` : '1'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handlePeriodDelta(1)}
                        disabled={Number(contractPeriod) >= PERIOD_MAX}
                        className={twJoin(
                          'flex h-5 w-5 items-center justify-center rounded-full border',
                          Number(contractPeriod) >= PERIOD_MAX
                            ? 'border-gray-20 text-gray-20'
                            : 'border-primary text-primary',
                        )}
                        aria-label={t('increasePeriod')}
                      >
                        <Plus width={10} height={10} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {PERIOD_PRESETS.map((month) => (
                      <button
                        key={month}
                        type="button"
                        className={twJoin(
                          'rounded-full border px-3 py-1 text-[12px]',
                          Number(contractPeriod) === month
                            ? 'border-primary text-primary'
                            : 'border-gray-30 text-gray-30',
                        )}
                        onClick={() => handleChangePeriod(month)}
                      >
                        {month} {t('months')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Search button */}
              <div className="shrink-0 px-4 pb-4 pt-1">
                <button
                  type="button"
                  className="flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-white"
                  onClick={handleSearch}
                >
                  <SearchIcon width={20} height={20} />
                  <span className="text-[15px] font-semibold">{t('search')}</span>
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default MobileSearchBottomSheet;
